import React, { useEffect, useState } from 'react';
import styles from './LastFiveYearsDisbProjApp.module.scss';
import { animateScroll } from 'react-scroll';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Indicator from '../../../models/Indicator';
import { isMobile } from '../../../services/booleanUtil';
import { BarChart } from '../../Portfolio/CardIndicator/Cards/BarChart/BarChart';
import { getProjectsByDate } from '../../../redux/actions/indicatorProjects';
import Table from './Table';
import { DISBURSEMENT_PROJECTION_LAST_5_YEARS_SELECTED_YEAR } from '../../../services/sessionFiltersIndicatorDetailHelper';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import SpinningLoader from '../../ProjectDetail/ProjectTabs/Loader/SpinningLoader';
import { IndicatorKeyEnum } from '../../../models/IndicatorKeyEnum';
import FilterIndicators from '../../../models/FilterIndicators';
import FiltersErrorMessage from '../../Util/Errors/FiltersErrorMessage/FiltersErrorMessage';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
// import { setIndicatorResponse } from '../../../redux/actions/dataTable';
// import { translateMultipleText } from '../../../services/stringUtil';
// import { emptyProjects } from '../../Portfolio/PortfolioUtil';
// import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
// import { arrayMove } from 'react-sortable-hoc';
// import { addIndicators } from '../../../redux/actions/indicators';
// import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import Filter from '../../Portfolio/Filter/Filter';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import useTable from '../../../hooks/useTable';
import ErrorMessageView from '../../Util/ErrorMessage/ErrorMessageView';
import { emptyBarChartData } from '../../Portfolio/IndicatorsHelpers/emptyIndicatorData';
import { TableContainer } from '@material-ui/core';

const baseColor = '#538CFF';
const lastYearColor = '#538CFF';
const graphFontFamily = 'Rubik';
const lineChartLabelColor = '#5F6B8A';
const chartStepSize = 5000000000;

type CheckedBox = {
  all: boolean;
  INV: boolean;
  PBL: boolean;
};

const updateChartColors = (selectedYear: string, labels: string[]) => {
  const colorsList = new Array(labels.length).fill(baseColor);
  colorsList[colorsList.length - 1] = lastYearColor;
  const newColorIndex = labels.indexOf(selectedYear);
  if (newColorIndex > -1) {
    colorsList[newColorIndex] = colorsList[newColorIndex].replace('1)', '0.4)');
  }
  return colorsList;
};
const ifIntegerFixedValue = value => (value % 1 !== 0 ? value.toFixed(0) : value);
const basicDataLabel = {
  formatter: (value, ctx) => {
    const showZeros = !!(ctx && ctx.chart && ctx.chart.data && ctx.chart.data.forceShowDataLabels);
    return value === null || value === undefined
      ? ''
      : value === 0
        ? showZeros
          ? '0'
          : ''
        : value < 1000000
          ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
          : `${ifIntegerFixedValue(value / 1000000)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  },
  anchor: 'end',
  clamp: false,
  align: 'end',
  // offset: 310,
  padding: 13,
  backgroundColor: 'transparent',
  color: context => {
    const { dataset, dataIndex, datasetIndex } = context;
    return dataIndex >= 0 && datasetIndex === 0
      ? '#a0abc1'
      : dataIndex >= 0 && datasetIndex === 1
        ? '#5a6a8a'
        : dataIndex >= 0 && datasetIndex === 2
          ? '#236bff'
          : lastYearColor;
  },
  clip: false,
  font: {
    size: isMobile() ? 8 : 13,
    family: graphFontFamily,
    weight: 'bold',
  },
};

export const variableOffset = (dataDatasets, context) => {
  let barchartDataset: any = null;
  if (dataDatasets && dataDatasets.find(i => !i.type)) {
    barchartDataset = dataDatasets.find(i => !i.type);
  }
  if (
    dataDatasets &&
    barchartDataset &&
    barchartDataset.data &&
    context &&
    context.dataIndex !== undefined &&
    barchartDataset.data[context.dataIndex] !== undefined &&
    context.dataset &&
    context.dataset.data &&
    context.dataset.data[context.dataIndex] !== undefined
  ) {
    const barchartValue = barchartDataset.data[context.dataIndex];
    const lineValue = context.dataset.data[context.dataIndex];
    if (barchartValue >= lineValue) {
      return barchartValue / lineValue < 1.1 ? -20 : -10;
    } else {
      return lineValue / barchartValue < 1.1 ? 0 : -10;
    }
  }
  return -10;
};

export const dataForDetail = data => {
  return data
    ? {
      ...data,
      datasets: data.datasets.map(i => {
        if (!i.type) {
          // Bar Chart
          i.barThickness = isMobile() ? 30 : 50;
          i.datalabels = basicDataLabel;
          return i;
        } else {
          // Line Chart
          i.borderWidth = 5;
          i.datalabels = {
            ...basicDataLabel,
            color: lineChartLabelColor,
            anchor: 'end',
            align: 'end',
            offset(context) {
              return variableOffset(data.datasets, context);
            },
          };
          return i;
        }
      }),
    }
    : null;
};
export const optionsforDetail = (t: Function, maxY?: undefined | number) => ({
  annotation: {
    annotations: [
      {
        backgroundColor: '#F1F3FF',
        drawTime: 'beforeDatasetsDraw',
        cornerRadius: 0,
        type: 'box',
        xScaleID: 'x-axis-0',
        xMin: 4 - 0.5,
        xMax: 4 + 0.5,
        borderColor: 'white',
        borderWidth: 4,
      },
    ],
  },
  tooltips: {
    enabled: true,
    backgroundColor: '#101319',
    bodyFontColor: '#ffffff',
    bodyFontFamily: 'Rubik',
    titleFontColor: '#ffffff',
    titleFontSize: 14,
    bodyFontSize: 14,
    callbacks: {
      title: (tooltipItem, data) => {
        return tooltipItem[0].xLabel + ' ' + data.yearsList[0];
      },
    },
  },
  legend: {
    display: false,
  },
  scales: {
    yAxes: [
      {
        display: true,
        gridLines: {
          display: true,
          borderDash: [4, 3],
          drawBorder: false,
        },
        ticks: {
          display: false,
          max: maxY,
          stepSize: chartStepSize,
        },
      },
    ],
    xAxes: [
      {
        display: true,
        gridLines: {
          display: false,
        },
        ticks: {
          fontSize: 16,
          fontColor: '#101319',
        },
      },
    ],
  },
  cornerRadius: 3,
});

const getEmptyChartDataForDetail = () => {
  const emptyData = emptyBarChartData();
  const dataWithDetail = dataForDetail(emptyData);
  return {
    ...dataWithDetail,
    isEmpty: true,
  };
};

const isInPortfolioDetail = (currentIndicator: Indicator, location, projectTypeFilterOnDetail) =>
  currentIndicator &&
  currentIndicator.indicatorKey !== 'default_indicator' &&
  location.pathname === '/portfoliodetail' &&
  projectTypeFilterOnDetail !== null;

const updateMaxIfExceedsGraph = (data, setMaxY: Function) => {
  let barchartData: any = null;
  if (data && data.datasets && data.datasets.find(i => !i.type)) {
    barchartData = data.datasets.find(i => !i.type);
  }
  if (barchartData && barchartData.data && barchartData.data.length) {
    const maxValue = Math.max(...barchartData.data);
    const prevMaxChartHeight = maxValue - (maxValue % chartStepSize) + chartStepSize;
    if (maxValue / prevMaxChartHeight > 0.95) {
      setMaxY(prevMaxChartHeight + chartStepSize);
    } else {
      setMaxY(undefined);
    }
  }
};

const hasSessionStorageVariableAndData = (item: string | null = null, data) => {
  return item && data;
};

const initialYearValue = () => sessionStorage.getItem(DISBURSEMENT_PROJECTION_LAST_5_YEARS_SELECTED_YEAR) || '';

const chartDataColorsUpdated = (chartData: any, newBackgroundColors: string[]) => {
  return {
    ...chartData,
    datasets: chartData.datasets.map(i => {
      if (i.label === 'bar') {
        i.backgroundColor = newBackgroundColors;
        return i;
      } else {
        return i;
      }
    }),
  };
};

const LastFiveYearsDisbProjApp = () => {
  const currentIndicator = useSelector<any, Indicator>(state => state.currentIndicator);
  const { filterIndicators } = useSelector<any, any>(state => state);
  const projectTypeFilterOnDetail = useSelector<any, any>(
    state => state.indicatorDetailFilter.projectTypeFilterOnDetail,
  );
  const [valueFilterTextDefault, setValueFilterTextDefault] = useState<string>('');
  const { handleClick, projects } = useTable();
  const [chartData, setChartData] = useState<any>(getEmptyChartDataForDetail());
  const [selectedYear, setSelectedYear] = useState<string>(initialYearValue());
  const { chartContainer, noResultsText } = styles;
  const dispatch = useDispatch();
  const [pblCheked, setPblCheked] = useState<boolean>(false);
  const [pblParam, setPblParam] = useState<any>(null);
  const [checkedRadio, setCheckedRadio] = useState<CheckedBox>({ all: true, INV: false, PBL: false });
  const [totalValuesHeader, setTotalValuesHeader] = useState({
    disburment: 0,
    INV: 0,
    PBL: 0,
  });

  const totalValues = () => {
    const totalDisburment = projects.reduce((a, v) => a + Number(v.field5), 0);
    const totalDisburmentINV = projects.filter(item => item.field6 === 'INV').reduce((a, v) => a + Number(v.field5), 0);
    const totalDisburmentPBL = projects.filter(item => item.field6 === 'PBL').reduce((a, v) => a + Number(v.field5), 0);

    setTotalValuesHeader({
      disburment: totalDisburment,
      INV: totalDisburmentINV,
      PBL: totalDisburmentPBL,
    });
  };

  //Filter
  const filterIndicatorsBar = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  // const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  // const [isLoading, setIsLoading] = React.useState(false);
  // const [totalProjects, setTotalProjects] = React.useState(false);

  // const handleCommonFilterChange = async () => {
  //   dispatch(setLoading(true));
  //   setIsLoading(true);
  //   // setLoadingIndicator(true);
  //   try {
  //     const indicatorsToProcess = await getNewFilteredIndicators();
  //     dispatch(setIndicatorResponse(indicatorsToProcess));
  //     startProcessIndicators(
  //       indicatorsToProcess,
  //       translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
  //       filterIndicatorsBar.common.projectTypeFilters,
  //     );
  //     setTotalProjects(emptyProjects(indicatorsToProcess.totalProjects));
  //   } catch (err) {
  //     console.error(err, 'Error fetching data from indicators');
  //   } finally {
  //     dispatch(setLoading(false));
  //     setIsLoading(false);
  //     // setLoadingIndicator(false);
  //   }
  // };

  // const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
  //   const result = processIndicators(inds, {
  //     titleFromTab,
  //     projectTypeFilter,
  //   });
  //   const elementSummary: any = result.find(elem => elem.id === 15);
  //   const indexSummary: number = result.indexOf(elementSummary);
  //   const sendEndSummary = arrayMove(result, indexSummary, result.length);
  //   dispatch(addIndicators(sendEndSummary));
  //   dispatch(
  //     setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'DISBURSEMENT_PROJECTION_LAST_5_YEARS')),
  //   );
  //   setIsLoading(false);
  // };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    if (
      filterIndicatorsBar.common.coReponsibleDivisions?.length > 0 ||
      filterIndicatorsBar.common.countryId?.length > 0 ||
      filterIndicatorsBar.common.divisionId?.length > 0 ||
      filterIndicatorsBar.common.interestIssues?.length > 0 ||
      filterIndicatorsBar.common.participantDivisions?.length > 0 ||
      filterIndicatorsBar.common.regionId?.length > 0 ||
      filterIndicatorsBar.common.sectors?.length > 0 ||
      filterIndicatorsBar.common.teamLeaders?.length > 0
    ) {
      try {
        getChartData();
      } catch (error) {
        console.error('Error in useEffect:', error);
        setHasError(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicatorsBar.common, pblParam]);

  useEffect(() => {
    // dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      filterIndicatorsBar.common.coReponsibleDivisions?.length === 0 &&
      filterIndicatorsBar.common.countryId?.length === 0 &&
      filterIndicatorsBar.common.divisionId?.length === 0 &&
      filterIndicatorsBar.common.interestIssues?.length === 0 &&
      filterIndicatorsBar.common.participantDivisions?.length === 0 &&
      filterIndicatorsBar.common.regionId?.length === 0 &&
      filterIndicatorsBar.common.sectors?.length === 0 &&
      filterIndicatorsBar.common.teamLeaders?.length === 0
    ) {
      try {
        getChartData();
      } catch (error) {
        console.error('Error in useEffect:', error);
        setHasError(true);
      }

      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicatorsBar.common, pblParam]);

  const handleClickBar = (year = '') => {
    setValueFilterTextDefault(year);
    setSelectedYear(year);
    setDisplayTable(true);
    sessionStorage.setItem(DISBURSEMENT_PROJECTION_LAST_5_YEARS_SELECTED_YEAR, year);
    setTimeout(() => {
      animateScroll.scrollToBottom({ smooth: true, duration: 800 });
    }, 50);
  };

  const handleChanges = e => {
    if (e.target.value === 'INV') {
      setPblParam('INV');
      setPblCheked(!pblCheked);
      setCheckedRadio({ all: false, INV: true, PBL: false });
    } else if (e.target.value === 'PBL') {
      setPblParam('PBL');
      setPblCheked(!pblCheked);
      setCheckedRadio({ all: false, INV: false, PBL: true });
    } else {
      setPblParam(null);
      setPblCheked(!pblCheked);
      setCheckedRadio({ all: true, INV: false, PBL: false });
    }
    // isChecked.value ? setPblParam('PBL') : setPblParam(null);
  };

  const { t } = useTranslation();
  const location = useLocation();
  const [displayTable, setDisplayTable] = useState<boolean>(false);
  const [maxY, setMaxY] = useState<undefined | number>(undefined);
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const getChartData = async () => {
    try {
      setLoadingData(true);
      setDisplayTable(false);
      setHasError(false);
      const sessionSelectedYear = sessionStorage.getItem(DISBURSEMENT_PROJECTION_LAST_5_YEARS_SELECTED_YEAR);

      const options = { indicatorId: currentIndicator.id, parameter: pblParam, forceDetail: true };

      const currentIndicatorOnDetail = await getNewFilteredIndicators(options);

      const justCurrentIndicatorOnDetail = {
        ...currentIndicatorOnDetail,
        indicatorsData: currentIndicatorOnDetail.indicatorsData.filter(i => i.id === currentIndicator.id),
      };
      const indicatorsProcessed = processIndicators(justCurrentIndicatorOnDetail);
      const data = dataForDetail(indicatorsProcessed[0]?.data);
      const chartDataToUse = data || getEmptyChartDataForDetail();

      if (hasSessionStorageVariableAndData(sessionSelectedYear, chartDataToUse)) {
        const newData = dataForDetail(chartDataToUse);
        updateChartData(newData, sessionSelectedYear, pblParam);
        setDisplayTable(true);
        setLoadingData(false);
      } else {
        updateMaxIfExceedsGraph(chartDataToUse, setMaxY);
        setChartData(chartDataToUse);
        setDisplayTable(chartDataToUse.isEmpty || false);
        setLoadingData(false);
      }
    } catch (error) {
      console.error('Error in getChartData:', error);
      setHasError(true);
      setErrorMessage((error as Error).message || 'Error al cargar los datos');
      setLoadingData(false);
      setDisplayTable(false);
    }
  };

  const handleRetry = () => {
    setHasError(false);
    setErrorMessage('');
    getChartData();
  };

  useEffect(() => {
    if (pblCheked || !pblCheked) {
      if ((selectedYear && chartData) || pblParam || !pblParam) {
        try {
          getChartData();
          updateChartData(chartData, selectedYear, pblParam);
        } catch (error) {
          console.error('Error in pblCheked useEffect:', error);
          setHasError(true);
        }
      } else if (chartData) {
        try {
          getChartData();
        } catch (error) {
          console.error('Error in pblCheked useEffect:', error);
          setHasError(true);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pblCheked, pblParam]);

  useEffect(() => {
    if (isInPortfolioDetail(currentIndicator, location, projectTypeFilterOnDetail)) {
      getChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectTypeFilterOnDetail]);

  useEffect(() => {
    if (selectedYear && chartData) {
      updateChartData(chartData, selectedYear, pblParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, filterIndicators, pblCheked]);

  useEffect(() => {
    if (projects.length > 0) totalValues();
  }, [projects]);

  const updateChartData = (chartData, selectedYear, pblParam) => {
    try {
      setLoadingTable(true);
      setHasError(false);
      dispatch(getProjectsByDate(6, parseInt(selectedYear), currentIndicator.id, pblParam));
      const newBackgroundColors = updateChartColors(selectedYear, chartData.labels);
      updateMaxIfExceedsGraph(chartData, setMaxY);
      setChartData(chartDataColorsUpdated(chartData, newBackgroundColors));
      setDisplayTable(true);
    } catch (error) {
      console.error('Error in updateChartData:', error);
      setHasError(true);
      setErrorMessage((error as Error).message || 'Error al actualizar los datos');
    } finally {
      setTimeout(() => {
        setLoadingTable(false);
      }, 1000);
    }
  };

  return (
    <>
      {currentIndicator.indicatorKey === IndicatorKeyEnum.DISBURSEMENT_PROJECTION_LAST_5_YEARS ? (
        <>
          <section className={styles['container-instruments']}>
            <legend>{t('new_instruments').toUpperCase()}</legend>
            <div>
              <input
                type="radio"
                name="drone"
                value="all"
                onClick={e => handleChanges(e)}
                checked={checkedRadio?.all}
              />
              <label>{t('new_see_all')}</label>
            </div>

            <div>
              <input
                type="radio"
                name="drone"
                value="INV"
                onClick={e => handleChanges(e)}
                checked={checkedRadio?.INV}
              />
              <label>{t('new_INV_only')}</label>
            </div>

            <div>
              <input
                type="radio"
                name="drone"
                value="PBL"
                onClick={e => handleChanges(e)}
                checked={checkedRadio?.PBL}
              />
              <label>{t('new_PBL_only')}</label>
            </div>
          </section>
        </>
      ) : null}

      <section className={chartContainer}>
        {hasError ? (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <ErrorMessageView
              title={t('title_error_message_view')}
              description={t('desc_error_message_tab')}
              reload={handleRetry}
              isIndicator={true}
            />
          </div>
        ) : !loadingData ? (
          <div>
            <BarChart
              type={currentIndicator.type}
              data={chartData}
              showSubLabels={!isMobile()}
              listPage={true}
              datasetClick={e => handleClickBar(e.label)}
              indicatorKey={currentIndicator.indicatorKey}
              chartOptions={optionsforDetail(t, maxY)}
              chartDimensions={{
                mobile: { width: 300, height: 200 },
                desktop: { width: 800, height: 400 },
              }}
            />
            <section className={`${styles['container-legent']}`}>
              <div className={`${styles['container-legent-disbursed']}`}>
                <p>
                  <span></span> {t('DI')}
                </p>
              </div>
              <div className={`${styles['container-legent-original']}`}>
                <p>
                  <span></span> {t('new_orginila_projection')}
                </p>
              </div>
              <div className={`${styles['container-legent-current']}`}>
                <p>
                  <span></span> {t('new_current_projection')}
                </p>
              </div>
            </section>
          </div>
        ) : (
          <SpinningLoader customStyle={{ marginBottom: 85 }} />
        )}
      </section>
      {displayTable && (
        <Table
          handleClickBarSelect={handleClickBar}
          valueFilterTextDefault={valueFilterTextDefault}
          loadingTable={loadingTable}
          totalValuesHeader={totalValuesHeader}
        />
      )}
      {(chartData && chartData.isEmpty && !loadingData) && (
        <div style={{ border: '2px solid #7A8AA9', borderRadius: '8px', width: '100%', height: 'auto', margin: '4rem 0rem 2rem 0rem' }}>
          <div style={{ height: '88px', backgroundColor: '#F1F3F8' }}></div>
          <div style={{ padding: '3rem 0rem' }}>
            <FiltersErrorMessage isTable={true} />
          </div>
        </div>
      )}
    </>
  );
};

export const ChartLabels = () => {
  const { t } = useTranslation();
  const { labelsContainer, labelLine, projIcon, lineText, disbIcon } = styles;
  return (
    <div className={labelsContainer}>
      <div className={labelLine}>
        <div className={projIcon}></div> <p className={lineText}>{t('original_projection')}</p>
      </div>
      <div className={labelLine}>
        <div className={disbIcon}></div> <p className={lineText}>{t('amount_disbursed')}</p>
      </div>
    </div>
  );
};

export default LastFiveYearsDisbProjApp;
